import { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../lib/api";
import { homeFor } from "../components/app/Guard";
import { AuthLayout, AuthField, AuthSubmit, AuthError } from "../components/app/AuthLayout";
import { BrandLine } from "../components/Brand";

export default function Login() {
  const { me, ready, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (ready && me) return <Navigate to={homeFor(me)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const user = await login(email.trim(), password);
      if (!user) throw new Error("Could not load your account");
      nav(homeFor(user), { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout tagline={<>One login.<br /><span className="text-[#39FF14]">Both sides</span><br />of the court.</>}>
      <form onSubmit={submit} className="space-y-6" data-testid="login-form">
        <div>
          <p className="font-mono-gd text-xs uppercase tracking-[0.25em] text-[#39FF14]">Coaches · Players · Recruiters</p>
          <h1 className="mt-3 font-display text-5xl font-black uppercase leading-none text-white sm:text-6xl">Tip-off.</h1>
          <p className="mt-3 text-slate-400">Use the same email and password you use in the GAMEDAY mobile app. We'll take you to the right dashboard.</p>
          <BrandLine className="mt-3" testid="login-brand-line" />
        </div>
        <AuthField label="Email" type="email" required autoComplete="email" data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@school.edu" />
        <div>
          <AuthField label="Password" type="password" required autoComplete="current-password" data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
          <Link to="/forgot-password" data-testid="login-forgot-link" className="mt-2 inline-block text-sm text-slate-400 underline decoration-[#39FF14]/50 hover:text-white">Forgot password?</Link>
        </div>
        <AuthError error={error} testid="login-error" />
        <AuthSubmit busy={busy} busyLabel="Checking the scorer's table…" testid="login-submit-button">
          Log in <ArrowRight className="h-4 w-4" />
        </AuthSubmit>
        <p className="text-sm text-slate-400">New to GAMEDAY? <Link to="/join" data-testid="login-join-link" className="font-semibold text-[#39FF14] hover:underline">Join now</Link></p>
        <p className="text-sm text-slate-400">Have an invite code? <Link to="/invite-code" data-testid="login-invite-code-link" className="font-semibold text-[#39FF14] hover:underline">Enter it here</Link></p>
        <p className="text-sm text-slate-400">Just looking? <Link to="/demo" data-testid="login-demo-link" className="font-semibold text-white underline decoration-[#39FF14]/60 hover:text-[#39FF14]">Explore a sample team</Link></p>
      </form>
    </AuthLayout>
  );
}
